<template>
  <div>
    <div class="row">
      <div class="col-md-12">
        <KTCodePreview v-bind:title="pageTitle">
          <template v-slot:preview>
            <b-form>
              <b-row class="mb-4">
                <b-col md="5">
                  <label class="form-label">
                    Tên nhóm <span class="text-danger">*</span>
                  </label>
                  <b-form-input
                    size="sm"
                    v-model="model.name"
                    placeholder="Nhập tên nhóm AdHoc"
                    autocomplete="off"
                  ></b-form-input>
                </b-col>
                <b-col md="7">
                  <label class="form-label">Mô tả</label>
                  <b-form-input
                    size="sm"
                    v-model="model.description"
                    placeholder="Mô tả nhóm"
                    autocomplete="off"
                  ></b-form-input>
                </b-col>
              </b-row>

              <!-- Thêm sản phẩm / danh mục -->
              <div class="section-title d-flex justify-content-between align-items-center">
                <span>Sản phẩm / Danh mục trong nhóm</span>
                <b-button variant="outline-primary" size="sm" @click="showImportModal">
                  <i class="far fa-file-excel mr-1"></i>Nhập Excel
                </b-button>
              </div>
              <b-row class="mb-3">
                <b-col md="3">
                  <label class="form-label">Tìm theo</label>
                  <b-form-select
                    size="sm"
                    v-model="searchType"
                    :options="searchTypeOptions"
                    value-field="id"
                    text-field="name"
                    @change="onSearchTypeChange"
                  ></b-form-select>
                </b-col>
                <b-col md="9">
                  <label class="form-label">
                    {{ isCategorySearch ? 'Danh mục' : 'Sản phẩm' }}
                  </label>
                  <Autosuggest
                    :key="'item-' + componentKey"
                    :model="searchText"
                    :suggestions="itemSuggestions"
                    :placeholder="
                      isCategorySearch
                        ? 'Nhập tên danh mục'
                        : 'Nhập tên hoặc mã sản phẩm'
                    "
                    :limit="10"
                    @select="onSelectItem($event)"
                    @change="onSearchItem($event)"
                    suggestionName="suggestionName"
                  />
                </b-col>
              </b-row>

              <b-table
                class="table-bordered table-hover mb-4"
                :fields="itemFields"
                :items="model.details"
                small
                show-empty
                empty-text="Chưa có sản phẩm / danh mục nào. Dùng ô tìm phía trên để thêm."
              >
                <template v-slot:cell(count)="row">
                  {{ row.index + 1 }}
                </template>
                <template v-slot:cell(type)="row">
                  <b-badge
                    :variant="
                      row.item.type === ITEM_TYPE.PRODUCT ? 'primary' : 'secondary'
                    "
                    >{{ itemTypeName(row.item.type) }}</b-badge
                  >
                </template>
                <template v-slot:cell(name)="row">
                  {{ row.item.name }}
                  <span v-if="row.item.code" class="text-muted">
                    ({{ row.item.code }})
                  </span>
                </template>
                <template v-slot:cell(actions)="row">
                  <b-button
                    variant="link"
                    size="sm"
                    class="text-danger p-0"
                    @click="removeItem(row.index)"
                  >
                    <i class="flaticon2-rubbish-bin-delete-button"></i>
                  </b-button>
                </template>
              </b-table>

              <div class="mt-4">
                <b-button
                  variant="primary"
                  size="sm"
                  class="mr-2"
                  :disabled="isSaving"
                  @click="save"
                >
                  <i class="flaticon2-check-mark mr-1"></i>
                  {{ isSaving ? 'Đang lưu...' : 'Lưu' }}
                </b-button>
                <b-button variant="secondary" size="sm" @click="goBack">
                  Huỷ
                </b-button>
              </div>
            </b-form>

            <!-- Nhập Excel: đọc file ở trình duyệt, gửi DANH SÁCH dòng lên API kiểm tra.
                 Sạch lỗi thì nút Kiểm tra nhường chỗ cho nút Import. -->
            <b-modal
              ref="import-modal"
              hide-footer
              size="lg"
              title="Nhập sản phẩm / danh mục từ Excel"
            >
              <div>
                Chọn file Excel gồm 3 cột <b>Loại</b>, <b>Mã</b>, <b>Tên</b> theo
                <b-link @click="downloadTemplate">
                  <i class="fas fa-download mr-1" style="font-size: 0.9rem"></i>file
                  mẫu</b-link
                >. Có Mã thì tìm theo Mã, không có thì tìm theo Tên.
              </div>
              <b-form-file
                placeholder="Hãy chọn một tập tin"
                @change="selectFile"
                v-model="file"
                ref="file-input"
                class="mt-3 mb-2"
              ></b-form-file>
              <div v-if="parsedRows.length" class="text-muted">
                Đọc được {{ parsedRows.length }} dòng.
              </div>

              <b-container class="mt-4 pl-0 pr-0" v-if="importErrors.length > 0">
                <b-alert show variant="danger" class="mb-2">
                  Có {{ importErrors.length }} dòng lỗi. Sửa file rồi chọn lại.
                </b-alert>
                <b-table
                  class="table-bordered table-hover"
                  :fields="errorFields"
                  :items="importErrors"
                  :per-page="IMPORT_PER_PAGE"
                  :current-page="currentErrorPage"
                  small
                ></b-table>
                <b-pagination
                  v-show="importErrors.length > IMPORT_PER_PAGE"
                  v-model="currentErrorPage"
                  :total-rows="importErrors.length"
                  :per-page="IMPORT_PER_PAGE"
                  align="right"
                ></b-pagination>
              </b-container>

              <b-container class="mt-4 pl-0 pr-0" v-if="canApplyImport">
                <b-alert show variant="success" class="mb-2">
                  Dữ liệu hợp lệ: {{ validatedItems.length }} dòng.
                </b-alert>
                <b-table
                  class="table-bordered table-hover"
                  :fields="importPreviewFields"
                  :items="validatedItems"
                  :per-page="IMPORT_PER_PAGE"
                  :current-page="currentPreviewPage"
                  small
                >
                  <template v-slot:cell(type)="row">
                    {{ itemTypeName(row.item.type) }}
                  </template>
                </b-table>
                <b-pagination
                  v-show="validatedItems.length > IMPORT_PER_PAGE"
                  v-model="currentPreviewPage"
                  :total-rows="validatedItems.length"
                  :per-page="IMPORT_PER_PAGE"
                  align="right"
                ></b-pagination>
              </b-container>

              <div class="mt-4">
                <b-button
                  v-if="canApplyImport"
                  variant="primary"
                  size="sm"
                  class="mr-2"
                  @click="applyImport"
                >
                  <i class="fas fa-file-import mr-1"></i>Import
                </b-button>
                <b-button
                  v-else
                  variant="primary"
                  size="sm"
                  class="mr-2"
                  :disabled="!parsedRows.length || isValidating"
                  @click="validateImport"
                >
                  {{ isValidating ? 'Đang kiểm tra...' : 'Kiểm tra dữ liệu' }}
                </b-button>
                <b-button variant="secondary" size="sm" @click="hideImportModal">
                  Huỷ
                </b-button>
              </div>
            </b-modal>
          </template>
        </KTCodePreview>
      </div>
    </div>
  </div>
</template>

<script>
/**
 * Tạo / cập nhật Nhóm AdHoc — SRS F-06.
 *
 * Cấu hình y hệt nhóm hoa hồng: mỗi item là Sản phẩm hoặc Danh mục.
 * Danh mục được nở ĐỆ QUY xuống mọi cấp con lúc engine tính.
 *
 * NT-3: dùng LẠI đúng API tìm kiếm mà nhóm hoa hồng đang dùng
 * (productSearch/search-by-product-category), không viết endpoint mới.
 */
import KTCodePreview from '@/view/content/CodePreview.vue';
import Autosuggest from '@/view/base/auto-sugguest/AutoSuggest.vue';
import { SET_BREADCRUMB } from '@/core/services/store/modules/breadcrumbs.module';
import ApiService from '@/core/services/api.service';
import debounce from 'debounce';
import xlsx from 'xlsx';
import { TIME_TRIGGER } from '@/utils/constants';
import { makeToastSuccess, makeToastFaile } from '@/utils/common';
import {
  COMMISSION_ADHOC_ITEM_TYPE,
  COMMISSION_ADHOC_ITEM_TYPE_NAME,
  COMMISSION_SEARCH_TYPE,
  toOptionList,
} from './commission.constants';
// thStyle + getErrorMessage nằm ở mixin của màn báo cáo — dùng lại, không tự viết
import commissionReportMixin, { thStyle } from './commissionReport.mixin';

const IMPORT_PER_PAGE = 10;
const IMPORT_COLUMN = Object.freeze({ TYPE: 'Loại', CODE: 'Mã', NAME: 'Tên' });

export default {
  components: { KTCodePreview, Autosuggest },
  mixins: [commissionReportMixin],
  data() {
    return {
      IMPORT_PER_PAGE,
      file: null,
      parsedRows: [],
      validatedItems: [],
      importErrors: [],
      isValidating: false,
      currentErrorPage: 1,
      currentPreviewPage: 1,
      errorFields: [
        { key: 'row', label: 'Dòng', thStyle: thStyle('12%') },
        { key: 'message', label: 'Lỗi' },
      ],
      importPreviewFields: [
        { key: 'type', label: 'Loại', thStyle: thStyle('18%') },
        { key: 'code', label: 'Mã', thStyle: thStyle('27%') },
        { key: 'name', label: 'Tên' },
      ],
      ITEM_TYPE: COMMISSION_ADHOC_ITEM_TYPE,
      componentKey: 0,
      isSaving: false,
      categoryId: null,
      model: { name: '', description: '', details: [] },
      searchType: COMMISSION_ADHOC_ITEM_TYPE.PRODUCT,
      searchText: '',
      itemSuggestions: [],
      searchTypeOptions: toOptionList(COMMISSION_ADHOC_ITEM_TYPE_NAME),
      itemFields: [
        {
          key: 'count',
          label: 'STT',
          thStyle: thStyle('8%'),
          tdClass: 'text-center',
        },
        { key: 'type', label: 'Loại', thStyle: thStyle('16%') },
        { key: 'name', label: 'Tên', thStyle: thStyle('70%') },
        {
          key: 'actions',
          label: '',
          thStyle: thStyle('6%'),
          tdClass: 'text-center',
        },
      ],
    };
  },
  computed: {
    isCategorySearch() {
      return this.searchType === COMMISSION_ADHOC_ITEM_TYPE.CATEGORY;
    },
    canApplyImport() {
      return this.validatedItems.length > 0 && this.importErrors.length === 0;
    },
    pageTitle() {
      return this.categoryId ? 'Cập nhật nhóm AdHoc' : 'Tạo nhóm AdHoc';
    },
  },
  created() {
    this.categoryId = Number(this.$route.query.id) || null;
    if (this.categoryId) {
      this.fetchDetail();
    }
  },
  mounted() {
    this.$store.dispatch(SET_BREADCRUMB, [
      { title: 'Hoa hồng', route: '/commission/categories' },
      { title: 'Nhóm AdHoc', route: '/commission/adhoc-categories' },
      { title: this.categoryId ? 'Cập nhật' : 'Tạo mới' },
    ]);
  },
  methods: {
    itemTypeName(type) {
      return COMMISSION_ADHOC_ITEM_TYPE_NAME[Number(type)] || '';
    },
    fetchDetail() {
      ApiService.setHeader();
      ApiService.query(`commission-adhoc-categories/${this.categoryId}`)
        .then(({ data }) => {
          const category = data.data || {};
          this.model = {
            name: category.name || '',
            description: category.description || '',
            details: (category.details || []).map((row) => ({
              type: Number(row.type),
              referenceId: row.referenceId,
              name: row.name || '',
              code: row.code || '',
            })),
          };
        })
        .catch((error) => {
          makeToastFaile(
            this.getErrorMessage(error, 'Không tải được nhóm'),
          );
        });
    },

    /* --------------------------- Tìm sản phẩm / danh mục -------------------- */
    onSearchTypeChange() {
      this.searchText = '';
      this.itemSuggestions = [];
      this.componentKey += 1;
    },
    onSearchItem(text) {
      this.searchText = text;
      this.debounceSearchItem(text, this.searchType);
    },
    debounceSearchItem: debounce(function (text, type) {
      if (!text) {
        this.itemSuggestions = [];
        return;
      }
      const typeSearch =
        Number(type) === COMMISSION_ADHOC_ITEM_TYPE.CATEGORY
          ? COMMISSION_SEARCH_TYPE.CATEGORY
          : COMMISSION_SEARCH_TYPE.PRODUCT;
      ApiService.setHeader();
      ApiService.get(
        `productSearch/search-by-product-category?searchProduct=${encodeURIComponent(
          text,
        )}&typeSearch=${typeSearch}&typeOfProduct=0`,
      )
        .then(({ data }) => {
          this.itemSuggestions = (data.data || []).map((item) => ({
            id: item.id,
            name: item.name || item.productName,
            code: item.code || '',
            suggestionName: item.code
              ? `${item.code} - ${item.name || item.productName}`
              : item.name || item.productName,
          }));
        })
        .catch(() => {
          this.itemSuggestions = [];
        });
    }, TIME_TRIGGER),
    onSelectItem(option) {
      const selected = option.item;
      const type = this.searchType;

      // Trùng thì bỏ qua — khớp UNIQUE KEY (nhóm, loại, referenceId) của bảng
      const existed = this.model.details.some(
        (row) =>
          Number(row.type) === Number(type) &&
          String(row.referenceId) === String(selected.id),
      );
      if (existed) {
        makeToastFaile('Sản phẩm / danh mục này đã có trong nhóm');
      } else {
        this.model.details.push({
          type,
          referenceId: String(selected.id),
          name: selected.name,
          code: selected.code,
        });
      }

      this.searchText = '';
      this.itemSuggestions = [];
      this.componentKey += 1;
    },
    removeItem(index) {
      this.model.details.splice(index, 1);
    },

    /* ----------------------------- Nhập Excel ------------------------------- */
    showImportModal() {
      this.resetImportState();
      this.$refs['import-modal'].show();
    },
    hideImportModal() {
      this.$refs['import-modal'].hide();
    },
    resetImportState() {
      this.file = null;
      this.parsedRows = [];
      this.validatedItems = [];
      this.importErrors = [];
      this.currentErrorPage = 1;
      this.currentPreviewPage = 1;
    },
    downloadTemplate() {
      const worksheet = xlsx.utils.aoa_to_sheet([
        [IMPORT_COLUMN.TYPE, IMPORT_COLUMN.CODE, IMPORT_COLUMN.NAME],
        [COMMISSION_ADHOC_ITEM_TYPE_NAME[COMMISSION_ADHOC_ITEM_TYPE.PRODUCT], '', ''],
        [COMMISSION_ADHOC_ITEM_TYPE_NAME[COMMISSION_ADHOC_ITEM_TYPE.CATEGORY], '', ''],
      ]);
      worksheet['!cols'] = [{ wch: 14 }, { wch: 28 }, { wch: 60 }];
      const workbook = xlsx.utils.book_new();
      xlsx.utils.book_append_sheet(workbook, worksheet, 'Nhom AdHoc');
      xlsx.writeFile(workbook, 'Mau_import_nhom_AdHoc.xlsx');
    },
    /** Chữ ở cột Loại -> mã loại; chữ lạ -> null để API báo lỗi đúng dòng */
    toImportType(value) {
      const text = String(value === undefined || value === null ? '' : value)
        .normalize('NFC')
        .trim()
        .toLowerCase();
      const matched = Object.keys(COMMISSION_ADHOC_ITEM_TYPE_NAME).find(
        (type) => COMMISSION_ADHOC_ITEM_TYPE_NAME[type].toLowerCase() === text,
      );
      return matched ? Number(matched) : null;
    },
    selectFile(event) {
      this.parsedRows = [];
      this.validatedItems = [];
      this.importErrors = [];
      this.currentErrorPage = 1;
      this.currentPreviewPage = 1;
      const selectedFile = event.target.files[0];
      if (!selectedFile) {
        return;
      }
      if (!/\.(xls|xlsx)$/.test(selectedFile.name.toLowerCase())) {
        this.file = null;
        this.$refs['file-input'].reset();
        makeToastFaile(
          'Định dạng file không hợp lệ. Vui lòng chọn file .xls hoặc .xlsx',
        );
        return;
      }
      const fileReader = new FileReader();
      fileReader.onload = (ev) => {
        try {
          const workbook = xlsx.read(ev.target.result, { type: 'binary' });
          const sheetName = workbook.SheetNames[0];
          const rows = xlsx.utils.sheet_to_json(workbook.Sheets[sheetName], {
            blankRows: false,
          });
          if (!rows.length) {
            makeToastFaile('File không có dòng dữ liệu nào!');
            return;
          }
          this.parsedRows = rows.map((row) => ({
            row: row.__rowNum__ + 1, // số dòng thật trong file Excel
            type: this.toImportType(row[IMPORT_COLUMN.TYPE]),
            code:
              row[IMPORT_COLUMN.CODE] !== undefined
                ? String(row[IMPORT_COLUMN.CODE]).trim()
                : '',
            name:
              row[IMPORT_COLUMN.NAME] !== undefined
                ? String(row[IMPORT_COLUMN.NAME]).trim()
                : '',
          }));
        } catch (error) {
          makeToastFaile('Không đọc được nội dung file Excel!');
        }
      };
      fileReader.readAsBinaryString(selectedFile);
    },
    validateImport() {
      if (!this.parsedRows.length) {
        return;
      }
      this.isValidating = true;
      this.validatedItems = [];
      this.importErrors = [];
      this.currentErrorPage = 1;
      this.currentPreviewPage = 1;
      ApiService.setHeader();
      ApiService.post('commission-adhoc-categories/validate-import', {
        rows: this.parsedRows,
      })
        .then(({ data }) => {
          const result = data.data || {};
          this.importErrors = result.errors || [];
          this.validatedItems = result.validItems || [];
        })
        .catch((error) => {
          makeToastFaile(
            this.getErrorMessage(error, 'Kiểm tra dữ liệu thất bại!'),
          );
        })
        .finally(() => {
          this.isValidating = false;
        });
    },
    /** Đổ dòng hợp lệ vào bảng của nhóm — CHƯA lưu, người dùng bấm Lưu mới ghi */
    applyImport() {
      if (!this.canApplyImport) {
        return;
      }
      let duplicated = 0;
      this.validatedItems.forEach((item) => {
        const existed = this.model.details.some(
          (row) =>
            Number(row.type) === Number(item.type) &&
            String(row.referenceId) === String(item.referenceId),
        );
        if (existed) {
          duplicated += 1;
          return;
        }
        this.model.details.push({
          type: item.type,
          referenceId: String(item.referenceId),
          name: item.name,
          code: item.code,
        });
      });
      const added = this.validatedItems.length - duplicated;
      makeToastSuccess(
        duplicated
          ? `Đã thêm ${added} dòng, bỏ qua ${duplicated} dòng đã có trong nhóm.`
          : `Đã thêm ${added} dòng từ file Excel.`,
      );
      this.hideImportModal();
    },

    /* -------------------------------- Lưu ----------------------------------- */
    save() {
      const name = (this.model.name || '').trim();
      if (!name) {
        return makeToastFaile('Vui lòng nhập tên nhóm');
      }
      if (this.model.details.length === 0) {
        return makeToastFaile('Vui lòng chọn ít nhất một sản phẩm hoặc danh mục');
      }

      const payload = {
        name,
        description: this.model.description,
        details: this.model.details.map((row) => ({
          type: row.type,
          referenceId: row.referenceId,
        })),
      };

      this.isSaving = true;
      ApiService.setHeader();
      const request = this.categoryId
        ? ApiService.put(`commission-adhoc-categories/${this.categoryId}`, payload)
        : ApiService.post('commission-adhoc-categories', payload);

      request
        .then(({ data }) => {
          makeToastSuccess(data.message);
          this.goBack();
        })
        .catch((error) => {
          makeToastFaile(
            this.getErrorMessage(error, 'Không lưu được nhóm'),
          );
        })
        .finally(() => {
          this.isSaving = false;
        });
    },
    goBack() {
      this.$router.push({ name: 'commission-adhoc-categories' });
    },
  },
};
</script>

<style scoped>
.form-label {
  display: block;
  font-size: 12px;
  color: #7e8299;
  margin-bottom: 4px;
}
.section-title {
  font-size: 14px;
  font-weight: 600;
  color: #181c32;
  padding-bottom: 8px;
  margin-bottom: 16px;
  border-bottom: 1px solid #ebedf3;
}
</style>
